import React from "react";
import { Link } from "react-router-dom";
import {
  Megaphone,
  Tv,
  PlayCircle,
  ArrowUpRight,
  ArrowLeft,
} from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

const DEMOS = [
  {
    key: "advertise",
    num: "01",
    title: "Mirrorloops Advertise",
    name: "DOOH campaign demo",
    desc: "Pick a city, set a budget, watch live impressions tick up on a simulated DOOH screen — no signup required.",
    icon: Megaphone,
    accent: "#00E5FF",
    cta: "Launch a demo campaign",
    to: "/demo/advertise",
  },
  {
    key: "signage",
    num: "02",
    title: "Mirrorloops Signage",
    name: "Dashboard tour",
    desc: "Click through screens, media, playlists, and analytics with pre-loaded sample data — exactly how operators run their fleet.",
    icon: Tv,
    accent: "#D4B878",
    cta: "Explore the dashboard",
    to: "/demo/signage",
  },
  {
    key: "player",
    num: "03",
    title: "Mirrorloops Signage Player",
    name: "Live player",
    desc: "Open the live demo player on this device — the exact same browser-based screen our operators ship to their venues today.",
    icon: PlayCircle,
    accent: "#CCFF00",
    cta: "Open the player",
    to: "/play/demo",
    external: true,
  },
];

export default function DemoIndex() {
  return (
    <div className="bg-[#0A0908] text-white min-h-screen font-sans">
      <header className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="demo-brand">
            <BrandLogo size={30} />
            <span className="font-display font-extrabold tracking-tight text-[16px]">
              Mirrorloops
            </span>
          </Link>
          <Link
            to="/"
            className="text-sm text-white/60 hover:text-white transition flex items-center gap-2"
            data-testid="demo-back-home"
          >
            <ArrowLeft size={14} /> Back home
          </Link>
        </div>
      </header>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="text-[11px] uppercase tracking-[0.22em] text-white/40 mb-4">
          Live demos · No signup
        </div>
        <h1 className="font-display font-black tracking-tighter leading-[0.92] text-5xl md:text-7xl">
          See Mirrorloops
          <br />
          in three minutes.
        </h1>
        <p className="mt-6 text-white/60 text-lg max-w-2xl">
          Three working, browser-based demos — one for each pillar. Try them
          on this device, or scan a QR to fire them up on your phone, tablet,
          or any nearby Smart TV.
        </p>

        <div className="mt-16 grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {DEMOS.map((d, i) => {
            const I = d.icon;
            const Comp = d.external ? "a" : Link;
            const props = d.external
              ? { href: d.to, target: "_blank", rel: "noreferrer" }
              : { to: d.to };
            return (
              <Comp
                key={d.key}
                {...props}
                data-testid={`demo-card-${d.key}`}
                className="group block bg-[#0A0908] hover:bg-white/[0.03] transition p-8 md:p-10 min-h-[420px] relative"
              >
                <div className="flex items-center justify-between mb-10">
                  <span
                    className="font-mono text-xs"
                    style={{ color: d.accent }}
                  >
                    {d.num} / {d.name}
                  </span>
                  <I size={20} style={{ color: d.accent }} />
                </div>
                <h3 className="font-display font-extrabold text-3xl md:text-[34px] tracking-tight leading-none mb-5">
                  {d.title}
                </h3>
                <p className="text-white/60 leading-relaxed text-sm md:text-base max-w-xs">
                  {d.desc}
                </p>
                <div
                  className="absolute left-8 md:left-10 bottom-8 md:bottom-10 inline-flex items-center gap-2 text-sm font-semibold transition group-hover:gap-3"
                  style={{ color: d.accent }}
                >
                  {d.cta} <ArrowUpRight size={14} />
                </div>
              </Comp>
            );
          })}
        </div>
      </section>

      <footer className="border-t border-white/10 py-8 text-center text-[11px] text-white/30">
        © Mirrorloops Opc Pvt. Ltd.
      </footer>
    </div>
  );
}
